import { useEffect, useState } from 'react'
import useInterval from './useInterval'

/**
 * A hook that manages a countdown timer, decreasing by 1 second every second until it reaches zero.
 * Returns the remaining time broken down into hours, minutes, and seconds.
 *
 * @param initialTotalRemainingSeconds - The initial number of seconds to count down from
 * @returns An object with hours, minutes, and seconds, or undefined if the countdown has finished or hasn't started
 *
 * @example
 * const result = useRemainingTime(174610)
 * Returns: { hours: 48, minutes: 30, seconds: 10 }
 */
function useRemainingTime(initialTotalRemainingSeconds: number) {
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)

  useEffect(() => {
    if (initialTotalRemainingSeconds <= 0) {
      setRemainingSeconds(null)
      return
    }
    setRemainingSeconds(initialTotalRemainingSeconds)
  }, [initialTotalRemainingSeconds])

  useInterval(
    () => {
      setRemainingSeconds(prev => {
        if (prev === null || prev <= 1) {
          return null
        }
        return prev - 1
      })
    },
    1000,
    { disabled: remainingSeconds === null },
  )

  if (!remainingSeconds) return

  const hours = Math.floor(remainingSeconds / 3600)
  const minutes = Math.floor((remainingSeconds % 3600) / 60)
  const seconds = remainingSeconds % 60

  return {
    hours,
    minutes,
    seconds,
  }
}

export default useRemainingTime
